import { LEARNING_OUTCOME_TRENDS } from '@/data/experience';
import { AnimatedCounter, ScrollReveal, SplitReveal } from '@/components/motion';

/** Format a learning-outcome value as "87.63%", or null as an accessible em dash. */
function formatValue(value: number | null): string {
  return value === null ? '—' : `${value.toFixed(2)}%`;
}

/** The two programme series drawn in every mini-chart and table column. */
const PROGRAMMES = [
  { key: 'mba', label: 'MBA', bar: 'bg-brand-navy' },
  { key: 'dba', label: 'DBA', bar: 'bg-brand-gold' },
] as const;

/**
 * OutcomesDetail (/experience/student-achievement-rates ★ ACBSP-mandated) —
 * "Learning outcomes". Renders the full LEARNING_OUTCOME_TRENDS grid: for each
 * of the two ACBSP outcomes, a block pairing an accessible dual-bar mini-chart
 * (MBA navy, DBA gold; a muted "—" placeholder where a year is not reported —
 * never a zero-height bar implying 0%) with a semantic Year / MBA / DBA table
 * carrying every value to two decimals as real, readable text.
 *
 * Motion: SplitReveal headline, ScrollReveal child-stagger across the two
 * outcome blocks, AnimatedCounter on each headline (2025) figure. Every
 * primitive is reduced-motion safe and never strands content hidden.
 *
 * Sourcing: figures are reproduced exactly from LEARNING_OUTCOME_TRENDS in
 * '@/data/experience' (institution-reported ACBSP MBA & DBA, 2022–2025);
 * nulls (DBA 2022, not reported) are handled everywhere and never imply 0%.
 */
export function OutcomesDetail() {
  return (
    <section className="bg-background py-16 md:py-24">
      <div className="mx-auto w-full max-w-6xl px-6">
        {/* ── Heading + lede ──────────────────────────────────────────── */}
        <header className="max-w-2xl">
          <p className="font-label text-xs tracking-[0.22em] text-brand-gold uppercase">
            Learning outcomes
          </p>
          <SplitReveal
            as="h2"
            by="lines"
            className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
          >
            ACBSP learning outcomes over time.
          </SplitReveal>
          <ScrollReveal from="up" delay={0.1}>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground md:text-lg">
              These track the percentage of students meeting each ACBSP learning outcome, across the
              MBA and DBA programmes, 2022–2025. A dash marks a year not reported.
            </p>
          </ScrollReveal>
        </header>

        {/* ── Two outcome blocks ──────────────────────────────────────── */}
        <ScrollReveal stagger={0.12} className="mt-12 grid grid-cols-1 gap-8 lg:grid-cols-2">
          {LEARNING_OUTCOME_TRENDS.map((outcome) => {
            const latest = outcome.series[outcome.series.length - 1];
            return (
              <div
                key={outcome.outcome}
                className="h-full rounded-2xl border border-border bg-background p-7 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-brand-gold/30 hover:shadow-xl motion-reduce:transition-none motion-reduce:hover:translate-y-0"
              >
                <h3 className="font-display text-xl leading-tight font-semibold text-foreground">
                  {outcome.outcome}
                </h3>

                {/* ── Latest-year headline pair ───────────────────────── */}
                <dl className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
                  {PROGRAMMES.map((programme) => {
                    const value = latest[programme.key];
                    return (
                      <div key={programme.key}>
                        <dt className="font-label text-xs tracking-[0.18em] text-muted-foreground uppercase">
                          {programme.label} · 2025
                        </dt>
                        <dd>
                          {value === null ? (
                            <span
                              className="font-display text-2xl leading-none font-semibold text-muted-foreground"
                              aria-label="not reported"
                            >
                              —
                            </span>
                          ) : (
                            <AnimatedCounter
                              value={value}
                              decimals={2}
                              suffix="%"
                              className="font-display text-2xl leading-none font-semibold text-brand-navy"
                            />
                          )}
                        </dd>
                      </div>
                    );
                  })}
                </dl>

                {/* ── Accessible dual-bar mini-chart ──────────────────── */}
                <div
                  className="mt-7 flex h-40 items-end justify-between gap-3"
                  role="img"
                  aria-label={`${outcome.outcome}: MBA and DBA achievement by year, 2022 to 2025. Full figures follow in the table below.`}
                >
                  {outcome.series.map((point) => (
                    <div
                      key={point.year}
                      className="flex h-full flex-1 flex-col items-center justify-end gap-2"
                    >
                      <div className="flex h-full w-full items-end justify-center gap-1.5">
                        {PROGRAMMES.map((programme) => {
                          const value = point[programme.key];
                          if (value === null) {
                            return (
                              <div
                                key={programme.key}
                                className="flex h-full flex-1 items-end justify-center"
                                aria-hidden
                              >
                                <span className="text-xs text-muted-foreground">—</span>
                              </div>
                            );
                          }
                          return (
                            <div
                              key={programme.key}
                              className="flex h-full flex-1 flex-col justify-end"
                            >
                              <div
                                className={`w-full rounded-t-md ${programme.bar}`}
                                style={{ height: `${value}%` }}
                                aria-hidden
                              />
                            </div>
                          );
                        })}
                      </div>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        {point.year}
                      </span>
                    </div>
                  ))}
                </div>

                {/* ── Legend ──────────────────────────────────────────── */}
                <div className="mt-4 flex items-center gap-5">
                  {PROGRAMMES.map((programme) => (
                    <span
                      key={programme.key}
                      className="inline-flex items-center gap-2 text-xs text-muted-foreground"
                    >
                      <span className={`size-2.5 rounded-sm ${programme.bar}`} aria-hidden />
                      {programme.label}
                    </span>
                  ))}
                </div>

                {/* ── Semantic figures table ──────────────────────────── */}
                <div className="mt-6 overflow-x-auto">
                  <table className="w-full border-collapse text-sm">
                    <caption className="sr-only">
                      {outcome.outcome}: percentage of students meeting the outcome, MBA and DBA,
                      2022 to 2025.
                    </caption>
                    <thead>
                      <tr className="border-b border-border">
                        <th
                          scope="col"
                          className="py-2 pr-4 text-left font-label text-xs tracking-[0.14em] text-muted-foreground uppercase"
                        >
                          Year
                        </th>
                        <th
                          scope="col"
                          className="py-2 px-4 text-right font-label text-xs tracking-[0.14em] text-muted-foreground uppercase"
                        >
                          MBA
                        </th>
                        <th
                          scope="col"
                          className="py-2 pl-4 text-right font-label text-xs tracking-[0.14em] text-muted-foreground uppercase"
                        >
                          DBA
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {outcome.series.map((point) => (
                        <tr key={point.year} className="border-b border-border last:border-b-0">
                          <th
                            scope="row"
                            className="py-2.5 pr-4 text-left font-medium text-foreground tabular-nums"
                          >
                            {point.year}
                          </th>
                          <td className="py-2.5 px-4 text-right tabular-nums">
                            {point.mba === null ? (
                              <span className="text-muted-foreground" aria-label="not reported">
                                —
                              </span>
                            ) : (
                              <span className="font-semibold text-brand-navy">
                                {formatValue(point.mba)}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 pl-4 text-right tabular-nums">
                            {point.dba === null ? (
                              <span className="text-muted-foreground" aria-label="not reported">
                                —
                              </span>
                            ) : (
                              <span className="font-semibold text-brand-navy">
                                {formatValue(point.dba)}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </ScrollReveal>
      </div>
    </section>
  );
}
